import bindAllMethods from '../../utils/bindAllMethods';
import { InternalService } from '../../core/services';
import IMonitoringService from './IMonitoringService';
import {
  DatadogInputType,
  SpanPropertiesType,
  SpanType,
  SplunkRumInputType
} from './types';
import DatadogService from './DatadogService';
import SplunkRumService from './SplunkRumService';

type MonitoringServiceConstructorProps = {
  enabled?: boolean;
  splunk?: SplunkRumInputType;
  datadog?: DatadogInputType;
};

type ProviderWithOptionalEnabled = IMonitoringService & {
  isEnabled?: () => boolean;
};

export default class MonitoringService
  extends InternalService
  implements IMonitoringService
{
  private _providers: IMonitoringService[];

  constructor(props: MonitoringServiceConstructorProps = {}) {
    super();
    this._providers = this._buildProviders(props);

    bindAllMethods(this);
    this._markAsReady();
  }

  private _buildProviders(
    props: MonitoringServiceConstructorProps
  ): IMonitoringService[] {
    if (!props?.enabled) return [];

    const providers: IMonitoringService[] = [];

    if (props.splunk) {
      providers.push(new SplunkRumService({ enabled: !!props.splunk.enabled }));
    }

    if (props.datadog) {
      providers.push(new DatadogService({ enabled: !!props.datadog.enabled }));
    }

    return providers;
  }

  private _isProviderEnabled(provider: IMonitoringService): boolean {
    const typedProvider = provider as ProviderWithOptionalEnabled;
    if (typeof typedProvider.isEnabled === 'function') {
      return !!typedProvider.isEnabled();
    }
    return true;
  }

  public startSpan(
    name: string,
    attributes: SpanPropertiesType['attributes']
  ): SpanType {
    const spans: SpanType[] = [];

    this._providers.forEach((provider) => {
      if (!this._isProviderEnabled(provider)) return;
      const span = provider?.startSpan?.(name, attributes);
      if (span) spans.push(span);
    });

    return {
      end: () => {
        spans.forEach((span) => span?.end?.());
      }
    };
  }

  public getSessionId(): string | undefined {
    for (const provider of this._providers) {
      if (!this._isProviderEnabled(provider)) continue;
      const sessionId = provider?.getSessionId?.();
      if (sessionId) return sessionId;
    }
    return undefined;
  }

  public isEnabled(): boolean {
    return this._providers.some((provider) =>
      this._isProviderEnabled(provider)
    );
  }
}
